/**
 * =============================================================================
 * ROASTBREW - PROGRESS HELPERS
 * =============================================================================
 * Per-user progress is the SOLE personal data layer. The catalog tree is
 * impersonal/cacheable and carries NO completion or progress. These pure helpers
 * merge the `progressByVideo` map (from useAuth) onto catalog nodes at RENDER
 * time to produce the personal view: completion ticks, progress bars, roll-up %.
 *
 * Pass the `progressByVideo` map in; never store the result on a content node.
 * =============================================================================
 */

// All leaf topic nodes under any catalog node (subject / course / chapter / section).
export function collectTopics(node) {
  if (!node) return [];
  if (node.topics) return node.topics;
  if (node.sections) return node.sections.flatMap(collectTopics);
  if (node.chapters) return node.chapters.flatMap(collectTopics);
  if (node.courses) return node.courses.flatMap(collectTopics);
  return [];
}

// Has the user completed this video?
export function videoCompleted(progressByVideo, videoId) {
  const p = videoId && progressByVideo ? progressByVideo[videoId] : null;
  return !!(p && p.completed);
}

// Percent watched for one video (completed => 100).
export function videoPct(progressByVideo, videoId) {
  const p = videoId && progressByVideo ? progressByVideo[videoId] : null;
  if (!p) return 0;
  if (p.completed) return 100;
  if (p.durationSeconds > 0) return Math.round((p.positionSeconds / p.durationSeconds) * 100);
  return 0;
}

// A saved position under this many seconds starts the video from the beginning instead of resuming there.
// Change the number here: it applies to every video (lessons, answer keys and the Library / Search hover previews).
export const RESUME_MIN_SECONDS = 30;

// Where playback should resume for one video, in seconds. Returns 0 when the
// video is completed (start over), has no saved progress, or was saved under
// RESUME_MIN_SECONDS. Used by the lesson / answer-key players AND the hover
// previews so they all resume from the same point.
export function videoResume(progressByVideo, videoId) {
  const p = videoId && progressByVideo ? progressByVideo[videoId] : null;
  if (!p || p.completed) return 0;
  const pos = p.positionSeconds || 0;
  return pos < RESUME_MIN_SECONDS ? 0 : pos;
}

// Completion roll-up for any node: % of its leaf topics the user has completed.
export function nodePct(node, progressByVideo) {
  const tops = collectTopics(node);
  if (!tops.length) return 0;
  const done = tops.filter((t) => videoCompleted(progressByVideo, t.videoId)).length;
  return Math.round((done / tops.length) * 100);
}

// A node counts as completed when it has topics and every one is completed.
export function nodeCompleted(node, progressByVideo) {
  const tops = collectTopics(node);
  return tops.length > 0 && tops.every((t) => videoCompleted(progressByVideo, t.videoId));
}
